import React, { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, FileText, Mail, Download, Paperclip, RefreshCw, Loader2, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#D4A84B';
const TOKEN_KEY = 'ulb_admin_token';

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const flatten = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return Object.entries(v).filter(([, x]) => x).map(([k, x]) => `${k}: ${x}`).join(', ');
  return String(v);
};

const buildBriefText = (item) => {
  const lines = [];
  lines.push('SYNERGOATOM SE — ULB PRIVATE PROJECT BRIEF');
  lines.push('='.repeat(52));
  lines.push(`Reference: ${item.id}`);
  lines.push(`Received: ${fmtDate(item.created_at)}`);
  lines.push('');
  lines.push('CONTACT & REPRESENTATION');
  lines.push('-'.repeat(52));
  Object.entries(item.contact || {}).forEach(([k, v]) => { if (v) lines.push(`${k}: ${v}`); });
  lines.push('');
  lines.push('PROJECT BRIEF');
  lines.push('-'.repeat(52));
  Object.entries(item.answers || {}).forEach(([k, v]) => {
    const val = flatten(v);
    if (val) lines.push(`${k}: ${val}`);
  });
  const files = item.files || [];
  if (files.length) {
    lines.push('');
    lines.push('ATTACHED DOCUMENTS');
    lines.push('-'.repeat(52));
    files.forEach((f) => lines.push(`- ${f.original_filename} (${f.content_type})`));
  }
  lines.push('');
  lines.push('CONFIDENTIAL — SYNERGOATOM SE');
  return lines.join('\n');
};

const downloadText = (filename, text) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(typeof data.detail === 'string' ? data.detail : 'Login failed'); return; }
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 font-['Space_Grotesk',sans-serif]">
      <form onSubmit={submit} className="w-full max-w-sm bg-slate-800/50 border border-slate-700 rounded-2xl p-8" data-testid="admin-login-form">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${GOLD}22` }}>
          <Lock className="w-6 h-6" style={{ color: GOLD }} />
        </div>
        <h1 className="text-2xl font-bold text-white font-['Syne',sans-serif]">Admin Access</h1>
        <p className="text-sm text-slate-400 mt-2 mb-6">SYNERGOATOM SE — private inbox</p>
        <label className="text-xs text-slate-500 uppercase tracking-wide">Email</label>
        <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          className="mt-1 mb-4 w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-[#D4A84B] outline-none" />
        <label className="text-xs text-slate-500 uppercase tracking-wide">Password</label>
        <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          className="mt-1 mb-6 w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-[#D4A84B] outline-none" />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-400 mb-4">{error}</p>}
        <button data-testid="admin-login-btn" type="submit" disabled={loading}
          className="w-full py-3 rounded-lg font-semibold text-slate-900 disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: GOLD }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}Sign in
        </button>
      </form>
    </div>
  );
};

const Inbox_ = ({ token, onLogout }) => {
  const [tab, setTab] = useState('briefs');
  const [briefs, setBriefs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [b, c] = await Promise.all([
        fetch(`${API}/api/admin/inquiries`, { headers }),
        fetch(`${API}/api/admin/contacts`, { headers }),
      ]);
      if (b.status === 401 || c.status === 401) { onLogout(); return; }
      setBriefs(await b.json());
      setContacts(await c.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const list = tab === 'briefs' ? briefs : contacts;

  return (
    <div className="min-h-screen bg-slate-900 text-white font-['Space_Grotesk',sans-serif]">
      <header className="border-b border-slate-800 sticky top-0 bg-slate-900/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Inbox className="w-5 h-5" style={{ color: GOLD }} />
            <h1 className="font-bold font-['Syne',sans-serif]">ULB Admin Inbox</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="text-slate-400 hover:text-white p-2" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={onLogout} data-testid="admin-logout" className="text-slate-400 hover:text-white flex items-center gap-2 text-sm"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 mb-6">
          <button onClick={() => { setTab('briefs'); setSelected(null); }} data-testid="tab-briefs"
            className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 border ${tab === 'briefs' ? 'text-slate-900 border-transparent' : 'text-slate-300 border-slate-700'}`}
            style={tab === 'briefs' ? { backgroundColor: GOLD } : {}}>
            <FileText className="w-4 h-4" />Bunker Briefs<span className="opacity-70">({briefs.length})</span>
          </button>
          <button onClick={() => { setTab('contacts'); setSelected(null); }} data-testid="tab-contacts"
            className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 border ${tab === 'contacts' ? 'text-slate-900 border-transparent' : 'text-slate-300 border-slate-700'}`}
            style={tab === 'contacts' ? { backgroundColor: GOLD } : {}}>
            <Mail className="w-4 h-4" />Contact<span className="opacity-70">({contacts.length})</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" />Loading…</div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* list */}
            <div className="space-y-2" data-testid="admin-list">
              {list.length === 0 && <p className="text-slate-500 text-sm">No entries yet.</p>}
              {list.map((item, i) => (
                <button key={item.id} onClick={() => setSelected(item)} data-testid={`${tab}-item-${i}`}
                  className={`w-full text-left bg-slate-800/50 border rounded-xl p-4 hover:border-[#D4A84B]/50 transition-colors ${selected && selected.id === item.id ? 'border-[#D4A84B]' : 'border-slate-700'}`}>
                  {tab === 'briefs' ? (
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{(item.contact && (item.contact.name || item.contact.email)) || 'Anonymous brief'}</span>
                        {(item.files || []).length > 0 && <span className="text-xs text-slate-400 flex items-center gap-1"><Paperclip className="w-3 h-3" />{item.files.length}</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{fmtDate(item.created_at)}</p>
                      <p className="text-sm text-slate-400 mt-2 line-clamp-1">{flatten((item.answers || {}).purpose) || flatten((item.answers || {}).philosophy) || '—'}</p>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{item.name || 'Unknown'}</span>
                        <span className="text-xs text-slate-500">{fmtDate(item.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{item.email}{item.organization ? ` · ${item.organization}` : ''}</p>
                      <p className="text-sm text-slate-400 mt-2 line-clamp-2">{item.message}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* detail */}
            <div className="lg:sticky lg:top-24 self-start">
              {!selected ? (
                <div className="bg-slate-800/30 border border-dashed border-slate-700 rounded-xl p-10 text-center text-slate-500">Select an entry to view details.</div>
              ) : tab === 'briefs' ? (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="brief-detail">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold font-['Syne',sans-serif]">Project Brief</h3>
                    <button onClick={() => downloadText(`ULB_Brief_${selected.id.slice(0, 8)}.txt`, buildBriefText(selected))}
                      data-testid={`export-brief-${selected.id}`} className="text-sm flex items-center gap-2 px-3 py-1.5 rounded-lg text-slate-900" style={{ backgroundColor: GOLD }}>
                      <Download className="w-4 h-4" />Export brief
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">{fmtDate(selected.created_at)} · Ref {selected.id.slice(0, 8)}</p>

                  {Object.keys(selected.contact || {}).length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Contact</p>
                      <div className="text-sm text-slate-300 space-y-1">
                        {Object.entries(selected.contact).filter(([, v]) => v).map(([k, v]) => (
                          <p key={k}><span className="text-slate-500">{k}:</span> {v}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selected.files || []).length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Attachments</p>
                      <div className="space-y-2">
                        {selected.files.map((f) => (
                          <a key={f.id} href={`${API}/api/bunker-inquiries/files/${f.id}/download`} target="_blank" rel="noopener noreferrer"
                            data-testid={`attachment-${f.id}`} className="flex items-center gap-2 text-sm text-[#D4A84B] hover:underline">
                            <Paperclip className="w-3.5 h-3.5" />{f.original_filename}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Answers</p>
                  <div className="text-sm text-slate-300 space-y-2 max-h-[50vh] overflow-auto pr-2">
                    {Object.entries(selected.answers || {}).filter(([, v]) => flatten(v)).map(([k, v]) => (
                      <div key={k} className="border-b border-slate-800 pb-2"><span className="text-slate-500">{k}:</span> {flatten(v)}</div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="contact-detail">
                  <h3 className="font-bold font-['Syne',sans-serif] mb-1">{selected.name}</h3>
                  <p className="text-xs text-slate-500 mb-4">{fmtDate(selected.created_at)}</p>
                  <div className="text-sm text-slate-300 space-y-2">
                    <p><span className="text-slate-500">Email:</span> <a href={`mailto:${selected.email}`} className="text-[#D4A84B] hover:underline">{selected.email}</a></p>
                    {selected.organization && <p><span className="text-slate-500">Organization:</span> {selected.organization}</p>}
                    {selected.phone && <p><span className="text-slate-500">Phone:</span> {selected.phone}</p>}
                    {selected.subject && <p><span className="text-slate-500">Subject:</span> {selected.subject}</p>}
                    <div className="mt-3 pt-3 border-t border-slate-800 whitespace-pre-wrap">{selected.message}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminInbox() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };
  if (!token) return <LoginForm onSuccess={setToken} />;
  return <Inbox_ token={token} onLogout={logout} />;
}
